import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchMachineProducts, submitSurvey, type MachineProduct } from '../api';
import { t } from '../i18n';
import { useOrder } from '../OrderContext';
import Header from '../components/Header';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Prefer the ordered product UUID; for custom mixes, rate the largest pour component. */
function resolveSurveyProductId(
  productId: string | number | null | undefined,
  customMix: Record<string, number>,
): string | null {
  if (typeof productId === 'string' && UUID_RE.test(productId)) return productId;

  const entries = Object.entries(customMix).filter(
    ([id, ml]) => ml > 0 && UUID_RE.test(id),
  );
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
}

const displayProductName = (p: MachineProduct): string =>
  t.machineProductNames[p.name] ?? t.productNames[p.product_id] ?? p.name;

const StarRow = ({
  value,
  onChange,
  label,
}: {
  value: number | null;
  onChange: (n: number) => void;
  label: string;
}) => (
  <div className="flex flex-col gap-2.5">
    <span className="text-[0.88rem] font-semibold text-[var(--cream)]">{label}</span>
    <div className="flex justify-between gap-1.5 sm:gap-2" role="radiogroup" aria-label={label}>
      {[1, 2, 3, 4, 5].map((n) => {
        const selected = value !== null && n <= value;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n}`}
            onClick={() => onChange(n)}
            className={`touch-target flex h-12 flex-1 items-center justify-center rounded-[12px] border text-[1.35rem] transition-all duration-150 active:scale-[0.96] sm:h-14 sm:text-[1.5rem] ${
              selected
                ? 'border-[var(--gold-lt)] bg-[var(--accent-pressed-bg)] text-[var(--gold-lt)] shadow-[0_0_16px_var(--accent-pressed-shadow)]'
                : 'border-[var(--border)] bg-[rgba(255,255,255,0.04)] text-[var(--muted)]'
            }`}
          >
            ★
          </button>
        );
      })}
    </div>
  </div>
);

const SurveyPage = () => {
  const navigate = useNavigate();
  const { order, resetOrder, updateOrder } = useOrder();

  const orderProductId = useMemo(
    () => resolveSurveyProductId(order.product?.id, order.customMix),
    [order.product?.id, order.customMix],
  );

  const [catalog, setCatalog] = useState<MachineProduct[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [pickedProductId, setPickedProductId] = useState<string | null>(null);

  const [rating, setRating] = useState<number | null>(null);
  const [serviceRating, setServiceRating] = useState<number | null>(null);
  const [wouldRecommend, setWouldRecommend] = useState<boolean | null>(null);
  const [age, setAge] = useState(
    order.user?.age != null && order.user.age >= 1 && order.user.age <= 120
      ? String(order.user.age)
      : '',
  );
  const [nationality, setNationality] = useState('');
  const [wantMoreTech, setWantMoreTech] = useState<boolean | null>(null);
  const [comment, setComment] = useState('');
  const [improvement, setImprovement] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const needsProductPicker = !orderProductId;

  useEffect(() => {
    if (!needsProductPicker) return;
    let cancelled = false;
    setCatalogLoading(true);
    fetchMachineProducts()
      .then((products) => {
        if (cancelled) return;
        const enabled = products.filter((p) => p.is_enabled);
        setCatalog(enabled);
      })
      .catch((err) => {
        console.warn('[SurveyPage] catalog fetch failed:', err);
        if (!cancelled) setCatalog([]);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [needsProductPicker]);

  const productId = orderProductId ?? pickedProductId;

  const productLabel = useMemo(() => {
    if (orderProductId && order.product?.name) return order.product.name;
    if (pickedProductId) {
      const p = catalog.find((c) => c.product_id === pickedProductId);
      if (p) return displayProductName(p);
    }
    return null;
  }, [orderProductId, order.product?.name, pickedProductId, catalog]);

  const ageNum = parseInt(age, 10);
  const ageValid = !isNaN(ageNum) && ageNum >= 1 && ageNum <= 120;
  const nationalityTrimmed = nationality.trim();

  const canSubmit =
    rating !== null &&
    serviceRating !== null &&
    wouldRecommend !== null &&
    wantMoreTech !== null &&
    ageValid &&
    nationalityTrimmed.length > 0 &&
    nationalityTrimmed.length <= 100 &&
    !!productId &&
    !submitting;

  const finish = () => {
    const user = order.user;
    resetOrder();
    if (user) updateOrder({ user });
    navigate(user ? '/menu-type' : '/', { replace: true });
  };

  const handleSkip = () => {
    finish();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !canSubmit ||
      rating === null ||
      serviceRating === null ||
      wouldRecommend === null ||
      wantMoreTech === null ||
      !productId
    ) {
      return;
    }

    setSubmitting(true);
    try {
      await submitSurvey({
        product_id: productId,
        rating,
        service_rating: serviceRating,
        would_recommend: wouldRecommend,
        age: ageNum,
        nationality: nationalityTrimmed,
        want_more_tech: wantMoreTech,
        comment: comment.trim() || undefined,
        improvement_suggestion: improvement.trim() || undefined,
      });
    } catch (err) {
      // Soft-fail: never block the guest after a successful pour path.
      console.warn('[SurveyPage] submit failed:', err);
    } finally {
      setSubmitting(false);
      setDone(true);
      window.setTimeout(finish, 1200);
    }
  };

  if (done) {
    return (
      <div className="kiosk-app rush-tech-bg relative flex h-full w-full flex-col overflow-hidden [animation:pageFadeIn_0.35s_ease_both]">
        <div className="noise-overlay" />
        <div className="relative z-10 flex flex-1 flex-col items-center justify-center gap-3 px-4 text-center">
          <span className="font-display text-[clamp(1.6rem,5vw,2.2rem)] font-bold text-[var(--cream)]">
            {t.surveyThanks}
          </span>
          <p className="text-[0.95rem] text-[var(--muted)]">{t.surveyThanksSub}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="kiosk-app rush-tech-bg relative flex h-full w-full flex-col overflow-hidden [animation:pageFadeIn_0.35s_ease_both]">
      <div className="noise-overlay" />
      <Header title={t.surveyTitle} onBack={() => navigate(-1)} />

      <div className="relative z-10 flex flex-1 flex-col overflow-y-auto px-4 py-5 sm:px-5 sm:py-6">
        <form
          onSubmit={handleSubmit}
          className="mx-auto my-auto flex w-full max-w-md flex-col gap-6"
        >
          <div className="text-center">
            {productLabel && (
              <p className="mb-1 text-[0.75rem] font-semibold uppercase tracking-[0.18em] text-[var(--gold-lt)]">
                {productLabel}
              </p>
            )}
            <p className="text-[0.95rem] text-[var(--muted)]">{t.surveySubtitle}</p>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="survey-nationality" className="text-[0.88rem] font-semibold text-[var(--cream)]">
              {t.surveyNationality}
            </label>
            <input
              id="survey-nationality"
              type="text"
              value={nationality}
              onChange={(e) => setNationality(e.target.value)}
              placeholder={t.surveyNationalityPlaceholder}
              maxLength={100}
              autoComplete="country-name"
              className="w-full rounded-[14px] border border-[var(--border)] bg-[rgba(255,255,255,0.06)] px-4 py-3.5 text-[1rem] text-white placeholder:text-[var(--muted)] outline-none transition-colors focus:border-[var(--gold-lt)]"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="survey-age" className="text-[0.88rem] font-semibold text-[var(--cream)]">
              {t.surveyAge}
            </label>
            <input
              id="survey-age"
              type="number"
              inputMode="numeric"
              min={1}
              max={120}
              value={age}
              onChange={(e) => setAge(e.target.value)}
              placeholder={t.surveyAgePlaceholder}
              className="w-full rounded-[14px] border border-[var(--border)] bg-[rgba(255,255,255,0.06)] px-4 py-3.5 text-[1rem] text-white placeholder:text-[var(--muted)] outline-none transition-colors focus:border-[var(--gold-lt)]"
            />
          </div>

          {needsProductPicker && (
            <div className="flex flex-col gap-2">
              <label htmlFor="survey-product" className="text-[0.88rem] font-semibold text-[var(--cream)]">
                {t.surveyPickDrink}
              </label>
              <select
                id="survey-product"
                value={pickedProductId ?? ''}
                onChange={(e) => setPickedProductId(e.target.value || null)}
                disabled={catalogLoading || catalog.length === 0}
                className="w-full appearance-none rounded-[14px] border border-[var(--border)] bg-[rgba(255,255,255,0.06)] px-4 py-3.5 text-[1rem] text-white outline-none transition-colors focus:border-[var(--gold-lt)] disabled:opacity-50"
              >
                <option value="">
                  {catalogLoading ? t.surveyLoadingDrinks : t.surveyPickDrinkPlaceholder}
                </option>
                {catalog.map((p) => (
                  <option key={p.product_id} value={p.product_id} className="text-black">
                    {displayProductName(p)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <StarRow
            label={t.surveyDrinkRating}
            value={rating}
            onChange={setRating}
          />

          <StarRow
            label={t.surveyServiceRating}
            value={serviceRating}
            onChange={setServiceRating}
          />

          <div className="flex flex-col gap-2.5">
            <span className="text-[0.88rem] font-semibold text-[var(--cream)]">
              {t.surveyRecommend}
            </span>
            <div className="grid grid-cols-2 gap-2.5" role="radiogroup" aria-label={t.surveyRecommend}>
              {[
                { value: true, label: t.surveyYes },
                { value: false, label: t.surveyNo },
              ].map(({ value, label }) => (
                <button
                  key={String(value)}
                  type="button"
                  role="radio"
                  aria-checked={wouldRecommend === value}
                  onClick={() => setWouldRecommend(value)}
                  className={`touch-target rounded-[14px] border py-3.5 text-[0.95rem] font-semibold transition-all duration-150 active:scale-[0.98] ${
                    wouldRecommend === value
                      ? 'border-[var(--gold-lt)] bg-[var(--accent-pressed-bg)] text-white shadow-[0_0_18px_var(--accent-pressed-shadow)]'
                      : 'border-[var(--border)] bg-[rgba(255,255,255,0.04)] text-[var(--muted)]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2.5">
            <span className="text-[0.88rem] font-semibold text-[var(--cream)]">
              {t.surveyWantMoreTech}
            </span>
            <div className="grid grid-cols-2 gap-2.5" role="radiogroup" aria-label={t.surveyWantMoreTech}>
              {[
                { value: true, label: t.surveyYes },
                { value: false, label: t.surveyNo },
              ].map(({ value, label }) => (
                <button
                  key={String(value)}
                  type="button"
                  role="radio"
                  aria-checked={wantMoreTech === value}
                  onClick={() => setWantMoreTech(value)}
                  className={`touch-target rounded-[14px] border py-3.5 text-[0.95rem] font-semibold transition-all duration-150 active:scale-[0.98] ${
                    wantMoreTech === value
                      ? 'border-[var(--gold-lt)] bg-[var(--accent-pressed-bg)] text-white shadow-[0_0_18px_var(--accent-pressed-shadow)]'
                      : 'border-[var(--border)] bg-[rgba(255,255,255,0.04)] text-[var(--muted)]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="survey-improvement" className="text-[0.88rem] font-semibold text-[var(--cream)]">
              {t.surveyImprovement}
            </label>
            <textarea
              id="survey-improvement"
              value={improvement}
              onChange={(e) => setImprovement(e.target.value)}
              placeholder={t.surveyImprovementPlaceholder}
              rows={3}
              maxLength={500}
              className="w-full resize-none rounded-[14px] border border-[var(--border)] bg-[rgba(255,255,255,0.06)] px-4 py-3.5 text-[1rem] text-white placeholder:text-[var(--muted)] outline-none transition-colors focus:border-[var(--gold-lt)]"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="survey-comment" className="text-[0.88rem] font-semibold text-[var(--cream)]">
              {t.surveyComment}
            </label>
            <textarea
              id="survey-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t.surveyCommentPlaceholder}
              rows={3}
              maxLength={500}
              className="w-full resize-none rounded-[14px] border border-[var(--border)] bg-[rgba(255,255,255,0.06)] px-4 py-3.5 text-[1rem] text-white placeholder:text-[var(--muted)] outline-none transition-colors focus:border-[var(--gold-lt)]"
            />
          </div>

          <div className="flex flex-col gap-3 pt-1">
            <button
              type="submit"
              disabled={!canSubmit}
              className="touch-target w-full rounded-[16px] border-2 border-[var(--confirm-border)] bg-[var(--confirm-bg)] py-4 font-display text-[1.05rem] font-bold uppercase tracking-[0.15em] text-white shadow-[0_0_18px_var(--confirm-shadow)] transition-opacity disabled:cursor-not-allowed disabled:opacity-40 active:opacity-80"
            >
              {submitting ? t.surveySubmitting : t.surveySubmit}
            </button>
            <button
              type="button"
              onClick={handleSkip}
              disabled={submitting}
              className="touch-target w-full rounded-[14px] border border-[var(--border)] bg-[var(--card)] py-3.5 text-[0.9rem] font-semibold text-[var(--muted-lt)] transition-opacity active:opacity-60 disabled:opacity-40"
            >
              {t.surveySkip}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SurveyPage;
